import { Navigate, Route, Routes } from "react-router-dom"
import { AuthProvider } from "./context/AuthProvider"
import { memo, Suspense, ReactNode } from "react"
import { AppShellProvider } from "./context/AppShellProvider"
import {
	ConditionalPageLayout,
	IsBrazil,
	PageWithHeaderBackButton,
	PageWithHeader,
	PageWithHeaderCloseButton
} from "@components/templates"
import { useTokenRefresh } from "@hooks/useTokenRefresh"
import { useRegistrationCleanup } from "@hooks/useRegistrationCleanup"
import { usePreloadAEMContent } from "@hooks/usePreloadAEMContent"
import { useLegalAcceptance } from "./pages/home/termsConditionsModal/hooks/useLegalAcceptance"
import { LegalAcceptance } from "./pages/home/termsConditionsModal/legalAcceptance"
import { VITE_USE_LEGAL_ACCEPTANCE } from "@utils/constants.util"
import AuthenticatedRoute from "@context/AuthenticatedRoute"
import {
	WelcomePage,
	authenticatedRoutes,
	standAloneRoutes,
	ErrorPage,
	pageWithHeaderBackButtonRoutes,
	pageWithHeaderCloseButtonRoutes,
	conditionalPageLayoutRoutes
} from "@context/Router"
import { RouteAnalytics } from "@context/Metadata"
import { HashScrollHandler } from "@components/navigation/HashScrollHandler"
import { AEMAllowedToolsProvider } from "@context/AEMAllowedToolsProvider"
import { useAllowedToolsFromAEM } from "@hooks/useAllowedToolsFromAEM"

const AppInitializer = memo(function AppInitializer({
	children
}: {
	children: ReactNode
}) {
	useAllowedToolsFromAEM()
	return <>{children}</>
})

function App() {
	return (
		<AppShellProvider>
			<AEMAllowedToolsProvider>
				<AuthProvider>
					<AppInitializer>
						<AppRouter />
					</AppInitializer>
				</AuthProvider>
			</AEMAllowedToolsProvider>
		</AppShellProvider>
	)
}
function AppRouter(): JSX.Element {
	useTokenRefresh()
	usePreloadAEMContent()
	useRegistrationCleanup()

	const { showTCModal, closeTCModal } = useLegalAcceptance()

	return (
		<>
			<RouteAnalytics />
			{VITE_USE_LEGAL_ACCEPTANCE && (
				<LegalAcceptance isOpen={showTCModal} onClose={closeTCModal} />
			)}
			<HashScrollHandler />
			<Suspense fallback={<div />}>
				<Routes>
					{standAloneRoutes.map(({ path, component: Component }) => (
						<Route key={path} path={path} element={<Component />} />
					))}
					{conditionalPageLayoutRoutes.map(({ path, component: Component }) => (
						<Route
							key={path}
							path={path}
							element={
								<ConditionalPageLayout>
									<Component />
								</ConditionalPageLayout>
							}
						/>
					))}

					{pageWithHeaderCloseButtonRoutes.map(
						({ path, component: Component }) => (
							<Route
								key={path}
								path={path}
								element={
									<PageWithHeaderCloseButton>
										<Component />
									</PageWithHeaderCloseButton>
								}
							/>
						)
					)}

					{pageWithHeaderBackButtonRoutes.map(
						({ path, component: Component }) => (
							<Route
								key={path}
								path={path}
								element={
									<PageWithHeaderBackButton>
										<Component />
									</PageWithHeaderBackButton>
								}
							/>
						)
					)}

					<Route element={<AuthenticatedRoute />}>
						<Route
							path="/contact"
							element={<Navigate to="/contact/royal" replace />}
						/>
						{authenticatedRoutes.map(({ path, component: Component }) => (
							<Route key={path} path={path} element={<Component />} />
						))}
					</Route>
					<Route path="*" element={<Navigate to={"/home"} replace />} />
					<Route
						key={"welcome"}
						path={"/welcome"}
						element={
							<IsBrazil>
								<PageWithHeader>
									<WelcomePage />
								</PageWithHeader>
							</IsBrazil>
						}
					/>
					<Route path="/error" element={<ErrorPage />} />
				</Routes>
			</Suspense>
		</>
	)
}

export default App
